import React, { useEffect, useState } from 'react';
import { FadeLoader } from 'react-spinners';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { Card, Stack, Button, Container, Typography, TablePagination, IconButton } from '@mui/material';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { ConfirmationDialog } from '../../components/general/ConfirmationDialog';
import Page from '../../components/Page';
import Iconify from '../../components/Iconify';
import { ListToolbar } from '../../components/tables';
import useQuery from '../../hooks/useQuery';
import { useApi } from '../../hooks/useApi';
import AgGridWrapper, { agDefaultColDef } from '../../components/tables/AgGridWrapper';

const BRAND = '#032C65';
const override = { margin: '0 auto' };

export default function AttendanceIndex() {
  const [attendances, setAttendances] = useState([]);
  const [selectedAttendance, setSelectedAttendance] = useState();
  const [pagination, setPagination] = useState({ total: 0, per_page: 10, current_page: 1, last_page: 1 });
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState('');
  const [sort, setSort] = useState('desc');
  const [sortField, setSortField] = useState('created_at');
  const [isLoading, setIsLoading] = useState(true);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const { doDelete } = useApi();

  const fetchData = useQuery();
  const { t } = useTranslation();

  const refreshTable = async () => {
    setIsLoading(true);
    await fetchData('attendance', filter, sort, setAttendances, sortField, setPagination, page, () => {});
    setIsLoading(false);
  };

  useEffect(() => {
    const delay = setTimeout(refreshTable, 500);
    return () => clearTimeout(delay);
  }, [filter, sort, page, sortField]);

  const handleDelete = async () => {
    doDelete(`attendance/${selectedAttendance.hashid}`, t('delete_success'), t('delete_error'), refreshTable);
    setShowDeleteDialog(false);
  };

  const columnDefs = [
    {
      field: 'date',
      headerName: t('date'),
      flex: 2,
      minWidth: 200,
      filter: 'agTextColumnFilter',
      valueFormatter: (params) => {
        try {
          return format(new Date(params.value), "EEEE d 'de' MMMM 'del' yyyy h:mm:ss a", { locale: es });
        } catch {
          return params.value;
        }
      },
    },
    { field: 'attendance', headerName: t('attendances'), flex: 1, minWidth: 120, filter: 'agTextColumnFilter' },
    { field: 'info', headerName: t('info'), flex: 2, minWidth: 150, sortable: false, filter: 'agTextColumnFilter' },
    {
      headerName: '',
      width: 100,
      sortable: false,
      filter: false,
      resizable: false,
      suppressMovable: true,
      cellRenderer: (params) => (
        <div style={{ display: 'flex', gap: 4 }}>
          <IconButton size="small" color="primary" component={RouterLink} to={`/dashboard/attendance/edit/${params.data.hashid}`}>
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" color="error" onClick={() => { setSelectedAttendance(params.data); setShowDeleteDialog(true); }}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </div>
      ),
    },
  ];

  return (
    <Page title={t('Attendances')}>
      <Container maxWidth="xl">
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4" gutterBottom>{t('Attendances')}</Typography>
          <Button
            variant="contained"
            component={RouterLink}
            to="/dashboard/attendance/save"
            startIcon={<Iconify icon="eva:plus-fill" />}
            sx={{ background: BRAND, '&:hover': { background: '#054a9e' }, borderRadius: 2, px: 3 }}
          >
            {t('new_attendance')}
          </Button>
        </Stack>
        <Card>
          <ListToolbar numSelected={0} filterName={filter} onFilterName={(e) => setFilter(e.target.value)} placeholder={t('attendances')} />
          {isLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
              <FadeLoader color="blue" loading cssOverride={override} size={50} />
            </div>
          ) : (
            <AgGridWrapper rowData={attendances} columnDefs={columnDefs} defaultColDef={agDefaultColDef} />
          )}
          <TablePagination
            component="div"
            rowsPerPageOptions={[5, 10, 25]}
            count={pagination.total}
            rowsPerPage={pagination.per_page || 10}
            page={page - 1}
            onPageChange={(e, newPage) => setPage(newPage + 1)}
          />
          <ConfirmationDialog open={showDeleteDialog} onClose={() => setShowDeleteDialog(false)} onConfirm={handleDelete} message={t('delete_confirm')} />
        </Card>
      </Container>
    </Page>
  );
}
